import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Button, Chip, Typography, Paper, Dialog, Slide, Grid,
  FormControl, FormLabel, TextField, Select, MenuItem, IconButton, Pagination,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FilterListIcon from "@mui/icons-material/FilterList";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import styled from "styled-components";
import { fetchData, postData, deleteData, putData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const StyledTextField = styled(TextField)(() => ({
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: "#D5D7DA", borderRadius: "10px" },
  },
}));

const fallbackGuidelines = [
  {
    id: 1,
    title: "Tamil Reading Project 2025",
    description: "Students will read and summarize Tamil literature passages from prescribed textbooks and present their understanding in written form.",
    tags: ["Planning", "Grade 3", "Published"],
    status: "published",
    date: "Jul 13, 2025",
  },
  {
    id: 2,
    title: "Tamil Writing Excellence Program",
    description: "A structured program to improve Tamil writing skills covering grammar, composition, and creative writing for all grade levels.",
    tags: ["Grade 4", "Published"],
    status: "published",
    date: "Jul 15, 2025",
  },
  {
    id: 3,
    title: "Oral Communication Assessment",
    description: "Guidelines for the oral communication assessment covering pronunciation, fluency, comprehension, and conversational Tamil.",
    tags: ["Planning", "Published"],
    status: "published",
    date: "Jul 20, 2025",
  },
  {
    id: 4,
    title: "Annual Science Fair Guidelines",
    description: "Comprehensive guidelines for the annual science fair including project structure, evaluation criteria, and submission deadlines.",
    tags: ["Grade 5", "Planning"],
    status: "draft",
    date: "Jul 25, 2025",
  },
];

const tagStyle = (tag) => {
  if (tag === "Published") return { backgroundColor: "#e8f5e9", color: "#4caf50" };
  if (tag === "Planning") return { backgroundColor: "#e3f2fd", color: "#1976d2" };
  return { backgroundColor: "#f3f0ff", color: "#244C77" };
};

const actionBtnStyle = {
  border: "1px solid #D5D7DA",
  borderRadius: "8px",
  color: "#8F95B2",
  textTransform: "none",
  fontSize: "13px",
  padding: "6px 14px",
};

const ITEMS_PER_PAGE = 5;

function GuidelineForm({ open, onClose, onSave, editItem }) {
  const [form, setForm] = useState({ title: "", description: "", tags: "", status: "published" });
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  useEffect(() => {
    if (editItem) {
      setForm({
        title: editItem.title || "",
        description: editItem.description || "",
        tags: Array.isArray(editItem.tags) ? editItem.tags.join(", ") : editItem.tags || "",
        status: editItem.status || "published",
      });
    } else {
      setForm({ title: "", description: "", tags: "", status: "published" });
    }
  }, [editItem, open]);

  const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title) return;
    const payload = {
      ...form,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
    };
    try {
      if (editItem?.id) {
        await putData(`admin/project-guidelines/${editItem.id}`, payload);
      } else {
        await postData("admin/project-guidelines", payload);
      }
      setToast({ open: true, severity: "success", message: editItem ? "Guideline updated" : "Guideline created" });
      setTimeout(() => { onSave(); onClose(); }, 1000);
    } catch (_) {
      onSave(payload, editItem?.id);
      onClose();
    }
  };

  const labelStyle = { color: "#8F95B2", fontSize: "13px" };

  return (
    <Dialog open={open} onClose={onClose} TransitionComponent={Transition} fullWidth maxWidth="sm">
      <Box component="form" onSubmit={handleSubmit} sx={{ p: "24px" }}>
        <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })} severity={toast.severity} message={toast.message} />
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography sx={{ fontWeight: "bold", fontSize: "16px" }}>
            {editItem ? "Edit Guideline" : "New Guideline"}
          </Typography>
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <FormLabel sx={labelStyle}>Title</FormLabel>
              <StyledTextField size="small" name="title" value={form.title} onChange={handleChange} placeholder="Guideline title" required />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <FormLabel sx={labelStyle}>Description</FormLabel>
              <StyledTextField size="small" name="description" multiline rows={4} value={form.description} onChange={handleChange} placeholder="Describe the guideline..." />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <FormLabel sx={labelStyle}>Tags (comma separated)</FormLabel>
              <StyledTextField size="small" name="tags" value={form.tags} onChange={handleChange} placeholder="e.g. Planning, Grade 3, Published" />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <FormLabel sx={labelStyle}>Status</FormLabel>
              <Select size="small" name="status" value={form.status} onChange={handleChange} sx={{ borderRadius: "10px" }}>
                <MenuItem value="published">Published</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="planning">Planning</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 3 }}>
          <Button onClick={onClose} sx={{ color: "#414651", textTransform: "none", px: 4, borderRadius: "10px", border: "1px solid #D5D7DA" }}>Cancel</Button>
          <Button type="submit" sx={{ background: "linear-gradient(90deg,#4A7BA8,#244C77)", color: "#fff", textTransform: "none", px: 4, borderRadius: "10px" }}>
            {editItem ? "Update" : "Create"}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}

export default function ProjectGuidelines() {
  const [guidelines, setGuidelines] = useState([]);
  const [page, setPage] = useState(1);
  const [formOpen, setFormOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const loadData = useCallback(async () => {
    try {
      const response = await fetchData("admin/project-guidelines?status=published&limit=10");
      if (response?.data && Array.isArray(response.data)) {
        setGuidelines(response.data);
      } else {
        setGuidelines(fallbackGuidelines);
      }
    } catch (_) {
      setGuidelines(fallbackGuidelines);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDelete = useCallback(async (item) => {
    try {
      await deleteData(`admin/project-guidelines/${item.id}`);
    } catch (_) {}
    setGuidelines((prev) => prev.filter((g) => g.id !== item.id));
    setToast({ open: true, severity: "success", message: "Guideline deleted" });
  }, []);

  const handleSave = useCallback((payload, editId) => {
    if (editId) {
      setGuidelines((prev) =>
        prev.map((g) => g.id === editId ? { ...g, ...payload } : g)
      );
    } else if (payload) {
      setGuidelines((prev) => [
        ...prev,
        { ...payload, id: Date.now(), date: new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) },
      ]);
    } else {
      loadData();
    }
  }, [loadData]);

  const openEdit = (item) => {
    setEditItem(item);
    setFormOpen(true);
  };

  const openCreate = () => {
    setEditItem(null);
    setFormOpen(true);
  };

  const totalPages = Math.ceil(guidelines.length / ITEMS_PER_PAGE);
  const displayedItems = guidelines.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  return (
    <Box sx={{ padding: "1rem", textAlign: "left" }}>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })} severity={toast.severity} message={toast.message} />

      <p style={{ color: "rgba(0,0,0,0.5)", fontWeight: "bold", fontSize: "14px", margin: 0 }}>
        Pages / Project Guidelines
      </p>
      <Box sx={{ mt: 1, mb: 1 }}>
        <h2 style={{ margin: 0 }}>Project Guidelines</h2>
        <Typography sx={{ color: "#8F95B2", fontSize: "13px", mt: 0.5 }}>
          Create and Manage Projects for Student & Teacher
        </Typography>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1 }}>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button startIcon={<DeleteOutlineIcon />} sx={actionBtnStyle}>Delete</Button>
          <Button startIcon={<FilterListIcon />} sx={actionBtnStyle}>Filters</Button>
          <Button startIcon={<FileDownloadIcon />} sx={actionBtnStyle}>Export</Button>
        </Box>
        <Button startIcon={<AddIcon />} onClick={openCreate} sx={{ background: "linear-gradient(90deg,#4A7BA8,#244C77)", color: "#fff", textTransform: "none", borderRadius: "8px", padding: "8px 20px", fontSize: "13px" }}>
          + New Guideline
        </Button>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {displayedItems.map((item, idx) => (
          <Paper key={idx} sx={{ p: "18px 24px", borderRadius: "12px", boxShadow: "0 1px 6px rgba(0,0,0,0.06)", "&:hover": { boxShadow: "0 4px 14px rgba(0,0,0,0.1)" } }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1 }}>
                  <Typography sx={{ fontWeight: "bold", fontSize: "15px", color: "#1a1a2e" }}>{item.title}</Typography>
                  {Array.isArray(item.tags) ? item.tags.map((tag, ti) => (
                    <Chip key={ti} label={tag} size="small" sx={{ ...tagStyle(tag), fontWeight: "bold", fontSize: "11px", borderRadius: "20px" }} />
                  )) : null}
                </Box>
                <Typography sx={{ fontSize: "13px", color: "#8F95B2", mb: 1, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                  {item.description}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: "#bbb" }}>Published {item.date}</Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => openEdit(item)}
                  sx={{ backgroundColor: "#e3f2fd", color: "#1976d2", borderRadius: "8px", textTransform: "none", fontSize: "12px", px: 1.5 }}
                >
                  Edit
                </Button>
                <IconButton
                  size="small"
                  onClick={() => handleDelete(item)}
                  sx={{ backgroundColor: "#ffebee", color: "#f44336", borderRadius: "8px", width: "34px", height: "34px" }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </Paper>
        ))}
      </Box>

      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, v) => setPage(v)}
            variant="outlined"
            shape="rounded"
            sx={{
              "& .MuiPaginationItem-root.Mui-selected": { background: "linear-gradient(90deg,#4A7BA8,#244C77)", color: "#fff", borderColor: "transparent" },
            }}
          />
        </Box>
      )}

      <GuidelineForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSave={handleSave}
        editItem={editItem}
      />
    </Box>
  );
}
